import { useCallback, useEffect, useRef, useState } from "react";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { PersonalDashboardPreferencesClient } from "@/api/personalDashboardPreferencesApi";

const client = new PersonalDashboardPreferencesClient();

/**
 * Loads and saves per-workspace dashboard preferences for the signed-in user.
 */
export function usePersonalPreferences() {
  const { activeWorkspaceId } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [theme, setThemeState] = useState("system");
  const [keycloakAccountUrl, setKeycloakAccountUrl] = useState(null);
  const [error, setError] = useState(null);
  const loadIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) {
      setLoading(false);
      return;
    }
    const loadId = ++loadIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const prefs = await client.get();
      if (loadId !== loadIdRef.current) return;
      setDisplayName(prefs.displayName || "");
      setThemeState(prefs.theme || "system");
      setKeycloakAccountUrl(prefs.keycloakAccountUrl);
    } catch (e) {
      if (loadId !== loadIdRef.current) return;
      setError(e instanceof Error ? e.message : "Could not load preferences.");
    } finally {
      if (loadId === loadIdRef.current) setLoading(false);
    }
  }, [activeWorkspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveProfile = useCallback(
    async (name) => {
      setSaving(true);
      setError(null);
      try {
        const prefs = await client.patch({ displayName: name });
        setDisplayName(prefs.displayName || "");
        setKeycloakAccountUrl(prefs.keycloakAccountUrl);
        return { ok: true };
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Could not save profile.";
        setError(msg);
        return { ok: false, message: msg };
      } finally {
        setSaving(false);
      }
    },
    []
  );

  const saveTheme = useCallback(async (nextTheme) => {
    setSaving(true);
    setError(null);
    try {
      const prefs = await client.patch({ theme: nextTheme });
      setThemeState(prefs.theme || nextTheme);
      setKeycloakAccountUrl(prefs.keycloakAccountUrl);
      return { ok: true, theme: prefs.theme || nextTheme };
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not save appearance.";
      setError(msg);
      return { ok: false, message: msg };
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    loading,
    saving,
    displayName,
    setDisplayName,
    theme,
    keycloakAccountUrl,
    error,
    setError,
    reload: load,
    saveProfile,
    saveTheme,
  };
}
